/**
 * Canonical URL + public route registry.
 *
 * Canonical host decision (documented in docs/release/WEB_SEO_RELEASE_READINESS.md):
 *   production serves three hosts — lookbel.com, www.lookbel.com, app.lookbel.com.
 *   The APEX (`https://lookbel.com`) is canonical for every public/marketing URL.
 *   `www.` 308-redirects to apex (see vercel.production.json) and `app.` stays
 *   reserved for the authenticated dashboard, which is noindex/Disallow anyway.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const { slugify } = require('./slug');

const ENVIRONMENTS = ['production', 'staging', 'qa', 'development'];

const DEFAULT_ORIGINS = {
  production: 'https://lookbel.com',
  staging: 'https://staging.lookbel.com',
  qa: 'https://qa.lookbel.com',
  development: 'http://localhost:3000',
};

/**
 * Anything that is not explicitly production is treated as non-production.
 * This is a deliberate fail-closed default: an unset/typo'd
 * REACT_APP_ENVIRONMENT must never produce an indexable build.
 *
 * @param {string | null | undefined} value
 * @returns {'production' | 'staging' | 'qa' | 'development'}
 */
function normalizeEnvironment(value) {
  const normalized = String(value == null ? '' : value).trim().toLowerCase();
  if (normalized === 'production' || normalized === 'prod' || normalized === 'main') {
    return 'production';
  }
  if (normalized === 'staging' || normalized === 'stage' || normalized === 'preview') {
    return 'staging';
  }
  if (normalized === 'qa' || normalized === 'test') return 'qa';
  return 'development';
}

/**
 * @param {string | null | undefined} environment
 * @param {string | null | undefined} [override] explicit REACT_APP_SEO_SITE_URL
 * @returns {string} origin with no trailing slash
 */
function getSiteOrigin(environment, override) {
  if (override) return String(override).replace(/\/+$/, '');
  return DEFAULT_ORIGINS[normalizeEnvironment(environment)];
}

/**
 * Normalize a path into its single canonical spelling:
 * leading slash, lowercase, no query/hash, no trailing slash (except root).
 *
 * This is the duplicate-URL hygiene rule: `/Locations/Texas/Austin/?sort=rating`
 * and `/locations/texas/austin` both canonicalize to `/locations/texas/austin`.
 *
 * @param {string | null | undefined} path
 * @returns {string}
 */
function normalizePath(path) {
  let value = String(path == null ? '' : path).trim();
  if (!value) return '/';
  // Drop query + fragment — never part of a canonical URL for these pages.
  value = value.split('#')[0].split('?')[0];
  if (!value.startsWith('/')) value = `/${value}`;
  value = value.toLowerCase();
  value = value.replace(/\/{2,}/g, '/');
  if (value.length > 1) value = value.replace(/\/+$/, '');
  return value || '/';
}

/**
 * @param {string} path
 * @param {{ environment?: string, siteUrl?: string }} [options]
 * @returns {string} absolute canonical URL
 */
function canonicalUrl(path, options) {
  const opts = options || {};
  return `${getSiteOrigin(opts.environment, opts.siteUrl)}${normalizePath(path)}`;
}

/* ------------------------------------------------------------------ *
 * Public route builders — the national URL hierarchy
 * ------------------------------------------------------------------ */

const ROUTES = {
  home: () => '/',
  about: () => '/about',
  contact: () => '/contact',
  serviceIndex: () => '/services',
  stylistDirectory: () => '/stylists',
  locationIndex: () => '/locations',

  /** @param {string} seoSlug */
  stylistProfile: (seoSlug) => `/stylists/${slugify(seoSlug)}`,
  /** @param {string} categorySlug */
  service: (categorySlug) => `/services/${slugify(categorySlug)}`,
  /** @param {string} stateSlug */
  state: (stateSlug) => `/locations/${slugify(stateSlug)}`,
  /** @param {string} stateSlug @param {string} city */
  city: (stateSlug, city) => `/locations/${slugify(stateSlug)}/${slugify(city)}`,
  /** @param {string} stateSlug @param {string} city @param {string} categorySlug */
  cityService: (stateSlug, city, categorySlug) =>
    `/locations/${slugify(stateSlug)}/${slugify(city)}/${slugify(categorySlug)}`,
};

/**
 * Path prefixes that must never be crawled or appear in a sitemap:
 * the authenticated app, auth flows, money flows, and admin tooling.
 * Order matters only for readability — matching is prefix-based.
 */
const DISALLOWED_PATH_PREFIXES = [
  '/client',
  '/stylist/', // legacy authenticated stylist dashboard (note: NOT '/stylists')
  '/admin',
  '/affiliate',
  '/dashboard',
  '/login',
  '/register',
  '/forgot-password',
  '/verify-email',
  '/booking',
  '/checkout',
  '/payment',
  '/payments',
  '/appointments',
  '/account',
  '/settings',
  '/security',
  '/notifications',
  '/chat',
  '/support',
  '/ref/',
  '/api/',
  '/home-old',
];

/**
 * Exact paths that are disallowed but have no trailing segment.
 */
const DISALLOWED_EXACT_PATHS = ['/stylist', '/ref'];

/**
 * Is this path part of the crawlable public surface?
 * Being crawlable is necessary but NOT sufficient for indexing — see
 * `eligibility.js`, which decides whether a crawlable page is thin.
 *
 * @param {string} path
 * @returns {boolean}
 */
function isCrawlablePublicPath(path) {
  const normalized = normalizePath(path);
  if (DISALLOWED_EXACT_PATHS.indexOf(normalized) !== -1) return false;
  for (let i = 0; i < DISALLOWED_PATH_PREFIXES.length; i += 1) {
    const prefix = DISALLOWED_PATH_PREFIXES[i];
    if (normalized === prefix.replace(/\/$/, '') || normalized.indexOf(prefix) === 0) {
      return false;
    }
  }
  return true;
}

/**
 * Static public pages that always exist regardless of marketplace inventory.
 * `changefreq`/`priority` are advisory only; Google ignores them, Bing reads them.
 */
const STATIC_PUBLIC_PAGES = [
  { path: '/', priority: '1.0', changefreq: 'daily' },
  { path: '/stylists', priority: '0.9', changefreq: 'daily' },
  { path: '/services', priority: '0.8', changefreq: 'weekly' },
  { path: '/locations', priority: '0.8', changefreq: 'weekly' },
  { path: '/about', priority: '0.5', changefreq: 'monthly' },
  { path: '/contact', priority: '0.4', changefreq: 'monthly' },
  { path: '/terms', priority: '0.2', changefreq: 'yearly' },
  { path: '/privacy', priority: '0.2', changefreq: 'yearly' },
  { path: '/delete-account', priority: '0.2', changefreq: 'yearly' },
  { path: '/provider-agreement', priority: '0.2', changefreq: 'yearly' },
  { path: '/cancellation-policy', priority: '0.2', changefreq: 'yearly' },
  { path: '/community-standards', priority: '0.2', changefreq: 'yearly' },
];

module.exports = {
  ENVIRONMENTS,
  DEFAULT_ORIGINS,
  DISALLOWED_PATH_PREFIXES,
  DISALLOWED_EXACT_PATHS,
  STATIC_PUBLIC_PAGES,
  ROUTES,
  normalizeEnvironment,
  getSiteOrigin,
  normalizePath,
  canonicalUrl,
  isCrawlablePublicPath,
};
